import React from 'react';
import { Empty, Button, Result } from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  ReloadOutlined, 
  ExclamationCircleOutlined,
  DisconnectOutlined,
  FileExclamationOutlined,
  LockOutlined
} from '@ant-design/icons';
import { ErrorInfo } from '../../utils/errorHandler';

export type EmptyStateType = 
  | 'default' 
  | 'search' 
  | 'filter' 
  | 'create' 
  | 'error' 
  | 'network' 
  | 'permission' 
  | 'notFound'
  | 'custom';

interface EmptyStateProps {
  type?: EmptyStateType;
  title?: string;
  description?: string;
  image?: React.ReactNode;
  action?: React.ReactNode;
  onAction?: () => void;
  actionText?: string;
  className?: string;
  error?: ErrorInfo;
  showRetry?: boolean;
  onRetry?: () => void;
}

/**
 * Helper function to get error title based on error type
 */
const getErrorTitle = (error: ErrorInfo): string => {
  switch (error.type) {
    case 'network':
      return '网络连接异常';
    case 'permission':
      return '权限不足';
    case 'business':
      return '操作失败';
    case 'system':
      return '系统错误';
    case 'validation':
      return '数据验证失败';
    default:
      return '发生错误';
  }
};

/**
 * Helper function to get error status for Result component
 */
const getErrorStatus = (error: ErrorInfo): 'error' | '403' | '404' | '500' => {
  switch (error.type) {
    case 'permission':
      return '403';
    case 'business':
      if (error.code === 'RESOURCE_NOT_FOUND') return '404';
      return 'error';
    case 'system':
      return '500';
    default:
      return 'error';
  }
};

const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'default',
  title,
  description,
  image,
  action,
  onAction,
  actionText,
  className = '',
  error,
  showRetry = false,
  onRetry,
}) => {
  // 根据类型设置默认值
  const getDefaultValues = () => {
    switch (type) {
      case 'search':
        return {
          title: title || '未找到匹配结果',
          description: description || '请尝试使用其他搜索关键词',
          icon: <SearchOutlined />,
          actionText: actionText || '清除搜索',
          resultStatus: undefined,
        };
      case 'filter':
        return {
          title: title || '未找到匹配结果',
          description: description || '请尝试调整筛选条件',
          icon: <SearchOutlined />,
          actionText: actionText || '清除筛选',
          resultStatus: undefined,
        };
      case 'create':
        return {
          title: title || '暂无数据',
          description: description || '点击下方按钮创建新数据',
          icon: <PlusOutlined />,
          actionText: actionText || '创建',
          resultStatus: undefined,
        };
      case 'error':
        return {
          title: title || '加载失败',
          description: description || '请尝试刷新页面',
          icon: <ReloadOutlined />,
          actionText: actionText || '刷新',
          resultStatus: 'error' as const,
        };
      case 'network':
        return {
          title: title || '网络连接异常',
          description: description || '请检查网络连接后重试',
          icon: <DisconnectOutlined />,
          actionText: actionText || '重试',
          resultStatus: 'error' as const,
        };
      case 'permission':
        return {
          title: title || '权限不足',
          description: description || '您没有访问此内容的权限',
          icon: <LockOutlined />,
          actionText: actionText || '返回',
          resultStatus: '403' as const,
        };
      case 'notFound':
        return {
          title: title || '内容不存在',
          description: description || '请求的内容不存在或已被删除',
          icon: <FileExclamationOutlined />,
          actionText: actionText || '返回',
          resultStatus: '404' as const,
        };
      case 'custom':
        return {
          title: title || '',
          description: description || '',
          icon: null,
          actionText: actionText || '',
          resultStatus: undefined,
        };
      default:
        return {
          title: title || '暂无数据',
          description: description || '',
          icon: null,
          actionText: actionText || '',
          resultStatus: undefined,
        };
    }
  };

  const { 
    title: defaultTitle, 
    description: defaultDescription, 
    icon, 
    actionText: defaultActionText,
    resultStatus
  } = getDefaultValues();

  // If error is provided, use error-specific display
  if (error) {
    const errorTitle = title || getErrorTitle(error);
    const errorDescription = description || error.message;
    const errorStatus = getErrorStatus(error);
    
    return (
      <div className={`empty-state error-state ${className}`}>
        <Result
          status={errorStatus}
          title={errorTitle}
          subTitle={errorDescription}
          extra={[
            showRetry && onRetry && (
              <Button key="retry" type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
                重试
              </Button>
            ),
            onAction && (
              <Button key="action" onClick={onAction}>
                {defaultActionText}
              </Button>
            ),
          ].filter(Boolean)}
        />
      </div>
    );
  }

  // Use Result component for error-like states
  if (resultStatus) {
    return (
      <div className={`empty-state ${className}`}>
        <Result
          status={resultStatus}
          title={defaultTitle}
          subTitle={defaultDescription}
          extra={
            (action || (onAction && defaultActionText)) && (
              action || (
                <Button type="primary" icon={icon} onClick={onAction}>
                  {defaultActionText}
                </Button>
              )
            )
          }
        />
      </div>
    );
  }

  // Default Empty component for normal empty states
  return (
    <div className={`empty-state ${className}`}>
      <Empty
        image={image || Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div>
            <div className="empty-state-title">{defaultTitle}</div>
            {defaultDescription && <div className="empty-state-description">{defaultDescription}</div>}
          </div>
        }
      />
      {(action || (onAction && defaultActionText)) && (
        <div className="empty-state-action">
          {action || (
            <Button type="primary" icon={icon} onClick={onAction}>
              {defaultActionText}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;